import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Chip, Icon, Loader, Typography } from '@saviynt/design-system';

import {
  SodApplicationGroup,
  SodFieldSet,
  SodRiskPriorityChip,
  SodSeverityChip,
  SodTextFieldSet,
} from '../../../../../../shared/components';
import {
  CF_SEC_BLD,
  ENTITLEMENT_DETAILS_TABS,
  violationsEntitlement,
} from '../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import { type RootState } from '../../../../../../shared/types/index.js';
import { useGetSodViolationsCountByStatusMutation } from '../../../../data-layer/detailsApi';
import { detailsActions } from '../../../../data-store/detailsSlice';
import { useEntitlementDetails } from '../../../../hooks';
import { ViolationDonutChart } from '../../../ViolationDonutChart';
import { type UserViolationChartDetails } from '../../UsersTab/data-layer';

import './Summary.css';

/**
 * RiskDetailsSummaryTab component for displaying comprehensive risk summary details.
 *
 * This component provides a detailed view of risk information in the AAG (Access Analytics
 * and Governance) module. It displays risk data with interactive charts, conflicting functions,
 * and comprehensive risk metadata for entitlement analysis.
 *
 * Features:
 * - Displays SOD violations count chart using real-time API data
 * - Shows conflicting job functions from selected entitlement risk
 * - Renders comprehensive risk metadata (score, priority, type, owner, description)
 * - Handles loading states with minimum display time for better UX
 * - Integrates with navigation to entitlement violations tab
 * - Supports localized content and formatting
 * - Always displays chart even with zero violation counts
 * - Uses proper error handling with fallback data
 *
 * @component
 * @returns {ReactElement} Risk summary tab with charts and detailed risk information
 */
function RiskDetailsSummaryTab(): ReactElement {
  const l = useInvertedLocalize();
  const { navigateToDetails } = useNavigateTo();

  const [getSodViolationsCount, { isLoading: isCountLoading }] =
    useGetSodViolationsCountByStatusMutation();
  const entitlementDetails = useEntitlementDetails();
  const [violationsCountData, setViolationsCountData] =
    useState<UserViolationChartDetails | null>(null);
  const requestInProgressRef = useRef<boolean>(false);
  const lastRequestKeyRef = useRef<string>('');

  const {
    isGlobalLoading,
    selectedEntitlementRisk,
    selectedEntitlement,
    selectedEntitlementData,
    entitlementRisksList,
    queryParam,
  } = useTypedSelector((state: RootState) => ({
    isGlobalLoading: state.aag.global.isGlobalLoading,
    selectedEntitlementRisk:
      state.aag.details.entitlementRisks.selectedEntitlementRisk,
    selectedEntitlement:
      state.aag.details.entitlementSummary.selectedEntitlement,
    selectedEntitlementData: state.aag.details.entitlementSummary.data,
    activeTab: state.aag.details.activeTab,
    entitlementRisksList: state.aag.details.entitlementRisks.list.data,
    queryParam: state.aag.details.queryParam,
  }));

  const dispatch = useDispatch();

  // Set selected risk from URL if not already selected
  useEffect(() => {
    if (
      !selectedEntitlementRisk &&
      queryParam &&
      entitlementRisksList.length > 0
    ) {
      const riskFromList = entitlementRisksList.find(
        (risk) => String(risk.riskId) === queryParam
      );

      if (riskFromList) {
        dispatch(detailsActions.setSelectedEntitlementRisks(riskFromList));
      }
    }
  }, [selectedEntitlementRisk, queryParam, entitlementRisksList, dispatch]);

  useEffect(() => {
    const defaultChartData: UserViolationChartDetails = {
      violationCount: 0,
      openCount: 0,
      inReviewCount: 0,
      remediatedCount: 0,
      mitigatedCount: 0,
      closedCount: 0,
    };

    setViolationsCountData(defaultChartData);

    const riskId = selectedEntitlementRisk?.riskId;
    const entitlementKey =
      entitlementDetails.entitlementDetails?.entitlementValueKey;
    const entitlementRiskIds = entitlementDetails.entitlementDetails?.riskIds;

    // Use selected risk ID or all entitlement risk IDs if no specific risk is selected
    const riskIds = riskId ? [riskId] : entitlementRiskIds ?? [];

    // Only make API call if we have both entitlementKey and riskIds, and avoid duplicate calls
    if (entitlementKey && riskIds.length > 0) {
      // Create a unique key for this request to prevent duplicate calls
      const requestKey = `${entitlementKey}-${riskIds.join(',')}`;

      // Skip if same request is already in progress or was just made
      if (
        requestInProgressRef.current ||
        lastRequestKeyRef.current === requestKey
      ) {
        return;
      }

      requestInProgressRef.current = true;
      lastRequestKeyRef.current = requestKey;

      getSodViolationsCount({
        filters: {
          risk: { riskIds },
          entitlement: {
            entitlementValueKeys: [entitlementKey],
          },
          ruleset: {},
          account: {},
          application: {},
          user: {},
          function: {},
          mitigatingControl: {},
          globalSearch: '',
        },
      })
        .unwrap()
        .then((data) => {
          requestInProgressRef.current = false;
          setViolationsCountData({
            violationCount:
              (data.openCount ?? 0) +
              (data.inReviewCount ?? 0) +
              (data.remediatedCount ?? 0) +
              (data.mitigatedCount ?? 0) +
              (data.closedCount ?? 0),
            openCount: data.openCount ?? 0,
            inReviewCount: data.inReviewCount ?? 0,
            remediatedCount: data.remediatedCount ?? 0,
            mitigatedCount: data.mitigatedCount ?? 0,
            closedCount: data.closedCount ?? 0,
          });
        })
        .catch(() => {
          requestInProgressRef.current = false;
          setViolationsCountData(defaultChartData);
        });
    }
  }, [
    selectedEntitlementRisk?.riskId,
    entitlementDetails.entitlementDetails?.entitlementValueKey,
    entitlementDetails.entitlementDetails?.riskIds,
    getSodViolationsCount,
  ]);

  if (isGlobalLoading || isCountLoading || violationsCountData === null) {
    return (
      <Box className='Sod-Violations-loaderWrapper'>
        <Loader kind='dots' format='inline' color='brand' />
      </Box>
    );
  }

  const functions = selectedEntitlementRisk
    ? [
        selectedEntitlementRisk.function1,
        selectedEntitlementRisk.function2,
        selectedEntitlementRisk.function3,
        selectedEntitlementRisk.function4,
        selectedEntitlementRisk.function5,
      ].filter(Boolean)
    : [];

  const handleViewViolations = () => {
    if (selectedEntitlementRisk) {
      const entitlementDisplayName =
        selectedEntitlement?.entitlementDisplayName ??
        selectedEntitlementData?.entitlementDisplayName;

      navigateToDetails({
        key: String(selectedEntitlementRisk.riskId),
        params: {
          title: selectedEntitlementRisk.riskName ?? '',
          type: violationsEntitlement,
          activeTab: ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
          entName: entitlementDisplayName ?? '',
          riskName: selectedEntitlementRisk.riskName ?? '',
        },
      });
    }
  };

  return (
    <Box className='Sod-RiskDetailsSummaryTab'>
      <Box className='Sod-RiskDetailsSummaryTab-chartWrapper'>
        <ViolationDonutChart
          item={violationsCountData}
          onViewViolations={
            (violationsCountData.violationCount ?? 0) > 0
              ? handleViewViolations
              : undefined
          }
        />
      </Box>
      <Box className='Sod-RiskDetailsSummaryTab-contentWrapper'>
        {functions.length > 0 && (
          <Box className='InherentRisksSidePanel-detailLinks'>
            <SodFieldSet
              label={l('Conflicting Job Functions')}
              value={
                <Box className='InherentRisksSidePanel-detailLinks-functions'>
                  {functions.map((functionValue) => (
                    <Box
                      key={functionValue}
                      className='InherentRisksSidePanel-detailLinks-functions-riskSidepanelItem'>
                      <Chip
                        label=''
                        color='warning'
                        kind='subtleFilled'
                        size='xSmall'
                        leftIcon={<Icon kind='JobFunction' />}
                      />
                      <Typography
                        kind='body2-bold'
                        color={CF_SEC_BLD}
                        lineLimit={1}>
                        {functionValue}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
            />
          </Box>
        )}
        {/* Field Sets */}
        <Box className='InherentRisksSidePanel-contentBody'>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            <SodFieldSet
              label={l('Risk Score')}
              value={
                <>
                  {selectedEntitlementRisk?.riskScore &&
                  selectedEntitlementRisk.riskScore !== 0 &&
                  selectedEntitlementRisk.riskSeverity ? (
                    <SodRiskPriorityChip
                      key='SodRiskPriorityChip'
                      priority={String(selectedEntitlementRisk.riskSeverity)}
                      riskScore={
                        (selectedEntitlementRisk.riskScore ||
                          selectedEntitlementRisk.riskViolationSummaryObjects
                            ?.riskScore) ??
                        null
                      }
                      isPrefixInterpoint={false}
                    />
                  ) : (
                    <Typography kind='body2' color={CF_SEC_BLD}>
                      {selectedEntitlementRisk?.riskScore ??
                        selectedEntitlementRisk?.riskViolationSummaryObjects
                          ?.riskScore}
                    </Typography>
                  )}
                </>
              }
            />
          </Box>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            <SodFieldSet
              label={l('Application')}
              value={
                <SodApplicationGroup
                  label={
                    selectedEntitlementRisk?.endpointName ??
                    selectedEntitlement?.endpointName ??
                    selectedEntitlementData?.endpointName ??
                    '-'
                  }
                  firstLogo={
                    selectedEntitlement?.endPointLogo ??
                    selectedEntitlementData?.endPointLogo ??
                    ''
                  }
                />
              }
            />
          </Box>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            <SodTextFieldSet
              label={l('Risk Description')}
              value={selectedEntitlementRisk?.description}
            />
          </Box>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            <SodTextFieldSet
              label={l('Ruleset')}
              value={selectedEntitlementRisk?.ruleset}
            />
          </Box>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            <SodTextFieldSet
              label={l('Business Process')}
              value={selectedEntitlementRisk?.businessProcess}
            />
          </Box>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            {selectedEntitlementRisk?.priority &&
            selectedEntitlementRisk.priority === l('CRITICAL') ? (
              <SodFieldSet
                label={l('Risk Priority')}
                value={
                  <Chip
                    size='xSmall'
                    kind='subtleFilled'
                    label={String(selectedEntitlementRisk.priority)}
                    color='pink'
                  />
                }
              />
            ) : (
              <SodFieldSet
                label={l('Risk Priority')}
                value={
                  <SodSeverityChip
                    text={String(selectedEntitlementRisk?.priority ?? 'None')}
                  />
                }
              />
            )}
          </Box>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            <SodTextFieldSet
              label={l('Risk Type')}
              value={selectedEntitlementRisk?.riskType}
            />
          </Box>
          <Box className='InherentRisksSidePanel-contentBody-rowItem'>
            <SodTextFieldSet
              label={l('Risk Owner')}
              value={selectedEntitlementRisk?.riskOwner}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default RiskDetailsSummaryTab;
